import {useMemo} from "react";
import {FiExternalLink} from "react-icons/fi";
import {Link, useParams} from "react-router";

import Button from "@/components/button/Button.tsx";
import {Chip} from "@/components/chip/Chip.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {Icon} from "@/components/icon/Icon.tsx";
import {TabContentContainer} from "@/components/layout/TabContentContainer.tsx";
import {dynamicTabConfig, TabConfig} from "@/components/layout/TabContentContainer.types.ts";
import {useAutoThrowSuspenseQuery} from "@/lib/hooks/useAutoThrowSuspenseQuery.ts";
import {TabContextProvider, useTabs} from "@/lib/hooks/useTabs.tsx";
import {GQL_STATUS_PAGE_QUERY} from "@/lib/queries/statuspages.ts";
import {StatusPage, StatusPageDisplay, StatusPageResponse, StatusPageVisibilityLevel} from "@/lib/types/statuspages.ts";
import {InspireThemeBanner} from "@/pages/statuspages/statuspages-manage/components/InspireThemeBanner.tsx";
import {StatusPageManageTabs} from "@/pages/statuspages/statuspages-manage/ManageStatusPageTabContainer.types.ts";
import {
  defaultStatusPageManageConfig,
  StaticStatusPageManageTabConfig,
  StatusPageProvider,
} from "@/pages/statuspages/statuspages-manage/ManageStatusPageTabContainer.utils.tsx";
import {getStatusPagePreviewURL} from "@/pages/statuspages/utils/urls.ts";
import {NotFoundPage} from "@/pages/website/NotFoundPage.tsx";

import styles from "./ManageStatusPageTabContainer.module.scss";

export const ManageStatusPageTabContainer = () => {
  const {brand} = useCurrentUserContext();
  const methods = useTabs({
    defaultTab: "general",
    blockerFunction: undefined,
    tabList: Object.values(StatusPageManageTabs), // this is all possible tabs and does not currently depend on visibility
  });
  const {tab, changeTab: setTab} = methods;

  const {id} = useParams();
  const spId = parseInt(id!);

  const {
    data: {statusPage, componentStatuses, testIDPs},
  } = useAutoThrowSuspenseQuery<StatusPageResponse>(GQL_STATUS_PAGE_QUERY, {
    variables: {
      id: spId,
    },
  });

  const sections: Record<string, TabConfig<void, StatusPage>> = useMemo(() => {
    if (!statusPage) {
      return {};
    }
    const derivedSections: Record<string, TabConfig<void, StatusPage>> = StaticStatusPageManageTabConfig;
    Object.keys(derivedSections).forEach((key) => {
      const tabKey = key as StatusPageManageTabs;
      const staticConfig = StaticStatusPageManageTabConfig[tabKey];

      const dynamicConfig: dynamicTabConfig = {
        ...defaultStatusPageManageConfig[tabKey],
      };

      if (statusPage.visibility_level === StatusPageVisibilityLevel.EXTERNAL_USERS) {
        if (tabKey === StatusPageManageTabs.EXTERNAL_USERS || tabKey === StatusPageManageTabs.SSO) {
          dynamicConfig.isVisible = true;
        }
      }

      if (staticConfig.getBadgeText) {
        const badgeText = staticConfig.getBadgeText(statusPage);
        if (badgeText) {
          dynamicConfig.badgeText = badgeText;
        }
      }

      derivedSections[tabKey] = {
        ...derivedSections[tabKey],
        ...dynamicConfig,
      };
    });
    return derivedSections;
  }, [statusPage]);

  if (!statusPage) {
    return (
      <NotFoundPage
        title="Status Page Not Found"
        message="The Status Page you are looking for does not exist."
        redirectComponent={
          <Button as={Link} to="/statuspages" className="mt-3">
            Go to Status Pages
          </Button>
        }
      />
    );
  }

  const viewURL = getStatusPagePreviewURL(brand, statusPage.id, statusPage.public_url);

  return (
    <StatusPageProvider statusPage={statusPage} componentStatuses={componentStatuses} testIDPs={testIDPs}>
      <TabContextProvider {...methods}>
        <TabContentContainer
          sections={sections}
          tab={tab}
          setTab={setTab}
          title={
            <span className={"d-flex align-items-center gap-2"}>
              {`Manage: ${statusPage.name}`}
              <Chip variant={"tag"} size={"smallLight"} className={"text-nowrap"}>
                {StatusPageDisplay[statusPage.visibility_level]}
              </Chip>
            </span>
          }
          tabContentClassName={sections[tab].formTab ? styles.formTabContent : styles.tabContainer}
          actionsBar={
            <div className={"d-flex align-items-center gap-2"}>
              <Button size={"large"} as={"a"} href={viewURL} target="_blank">
                Preview <Icon icon={FiExternalLink} />
              </Button>
            </div>
          }
        >
          <InspireThemeBanner statusPage={statusPage} formTab={sections[tab].formTab ?? false} liveUrl={viewURL} />
          {sections[tab].html()}
        </TabContentContainer>
      </TabContextProvider>
    </StatusPageProvider>
  );
};
